// src/modules/aiml/FaceAPISection.jsx
// face-api.js — Detection · 68 Landmarks · Expressions · Age/Gender · Recognition
// Tabs: overview | detect | landmarks | expressions | age_gender | recognition | concepts

import React, { useState, useRef, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { ModelLoader, InferenceTag } from './AIMLModule.jsx';

const ACCENT = '#f472b6';
const P = ['#f472b6','#f59e0b','#34d399','#60a5fa','#a78bfa','#fb923c','#ef4444','#4ade80'];
const TABS = ['overview','detect','landmarks','expressions','age_gender','recognition','concepts'];

const Btn = ({ onClick, children, color = ACCENT, disabled, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ padding: '8px 18px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: disabled ? 'not-allowed' : 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, opacity: disabled ? 0.4 : 1, ...style }}>{children}</button>
);
const Tag = ({ children, color = ACCENT }) => (
  <span style={{ padding: '2px 8px', background: color + '20', border: `1px solid ${color}44`, color, fontSize: '9px', borderRadius: '2px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{children}</span>
);
const Panel = ({ title, accent = ACCENT, children, style }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: '4px', overflow: 'hidden', ...style }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--b0)', fontSize: '9px', letterSpacing: '.1em', color: accent, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>{title}</div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

// ── face-api.js CDN ───────────────────────────────────────────────
const FACEAPI_CDN   = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
const MODELS_BASE   = 'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights';

let _faceApiLoaded  = false;

async function ensureFaceApi() {
  if (_faceApiLoaded && window.faceapi) return;
  await new Promise((res, rej) => {
    if (window.faceapi) { _faceApiLoaded = true; res(); return; }
    const s = document.createElement('script');
    s.src = FACEAPI_CDN; s.onload = () => { _faceApiLoaded = true; res(); }; s.onerror = rej;
    document.head.appendChild(s);
  });
}

const EMOTION_EMOJIS = {
  happy: '😊', sad: '😢', angry: '😠', fearful: '😨',
  disgusted: '🤢', surprised: '😲', neutral: '😐',
};
const EMOTION_COLORS = {
  happy: 'var(--green)', sad: 'var(--cyan)', angry: 'var(--red)',
  fearful: 'var(--violet)', disgusted: 'var(--orange)', surprised: 'var(--gold)', neutral: 'var(--t3)',
};

// ── Draw helpers ──────────────────────────────────────────────────
function drawDetections(canvas, imgEl, detections) {
  if (!canvas || !imgEl) return;
  const ctx = canvas.getContext('2d');
  canvas.width  = imgEl.naturalWidth;
  canvas.height = imgEl.naturalHeight;
  ctx.drawImage(imgEl, 0, 0);
  detections.forEach(({ detection }, i) => {
    const { box } = detection;
    const col = P[i % P.length];
    ctx.strokeStyle = col; ctx.lineWidth = 3;
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.fillStyle = col + 'cc';
    ctx.fillRect(box.x, box.y - 22, 100, 22);
    ctx.fillStyle = '#000'; ctx.font = 'bold 12px monospace';
    ctx.fillText(`Face ${i+1} ${(detection.score * 100).toFixed(0)}%`, box.x + 4, box.y - 5);
  });
}

function drawLandmarks(canvas, imgEl, allLandmarks) {
  if (!canvas || !imgEl) return;
  const ctx = canvas.getContext('2d');
  canvas.width  = imgEl.naturalWidth;
  canvas.height = imgEl.naturalHeight;
  ctx.drawImage(imgEl, 0, 0);
  const GROUPS = {
    jaw:       [0,16],  leftEyebrow: [17,21], rightEyebrow: [22,26],
    nose:      [27,35], leftEye:     [36,41], rightEye:     [42,47],
    mouth:     [48,67],
  };
  const GROUP_COLORS = {
    jaw: '#34d399', leftEyebrow: '#f59e0b', rightEyebrow: '#f59e0b',
    nose: '#60a5fa', leftEye: '#f472b6', rightEye: '#f472b6', mouth: '#fb923c',
  };

  allLandmarks.forEach(lm => {
    const pts = lm.positions;
    Object.entries(GROUPS).forEach(([group, [start, end]]) => {
      const col = GROUP_COLORS[group];
      // Connect
      ctx.beginPath();
      for (let i = start; i <= end; i++) {
        const p = pts[i];
        if (i === start) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      }
      if (['leftEye','rightEye','mouth'].includes(group)) ctx.closePath();
      ctx.strokeStyle = col + '88'; ctx.lineWidth = 1; ctx.stroke();
      // Dots
      for (let i = start; i <= end; i++) {
        const p = pts[i];
        ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = col; ctx.fill();
      }
    });
  });
}

// ═════════════════════════════════════════════════════════════════
export default function FaceAPISection({ gpuInfo }) {
  const [tab, setTab] = useState('overview');

  // Shared model state
  const [modelsLoaded, setModelsLoaded] = useState({
    ssd: false, tinyFace: false, landmarks68: false,
    expressions: false, ageGender: false, faceRec: false,
  });
  const [loadingModel, setLoadingModel] = useState(null);
  const [loadProgress, setLoadProgress] = useState(0);

  // Per-tab state
  const [detectImg, setDetectImg]     = useState(null);
  const [detectResult, setDetectResult] = useState(null);
  const [detectTime, setDetectTime]   = useState(null);
  const detectCanvas = useRef(null);

  const [lmImg, setLmImg]             = useState(null);
  const [lmResult, setLmResult]       = useState(null);
  const [lmTime, setLmTime]           = useState(null);
  const lmCanvas  = useRef(null);

  const [exprImg, setExprImg]         = useState(null);
  const [exprResult, setExprResult]   = useState(null);
  const [exprTime, setExprTime]       = useState(null);
  const exprCanvas = useRef(null);

  const [agImg, setAgImg]             = useState(null);
  const [agResult, setAgResult]       = useState(null);
  const [agTime, setAgTime]           = useState(null);
  const agCanvas  = useRef(null);

  const [recImg1, setRecImg1]         = useState(null);
  const [recImg2, setRecImg2]         = useState(null);
  const [recResult, setRecResult]     = useState(null);

  // ── Model loader ──────────────────────────────────────────────
  const loadModels = async (needed) => {
    await ensureFaceApi();
    const fa = window.faceapi;
    setLoadingModel(needed.join('+'));
    setLoadProgress(10);
    try {
      const loaders = [];
      if (needed.includes('ssd')          && !modelsLoaded.ssd)          loaders.push(fa.nets.ssdMobilenetv1.loadFromUri(MODELS_BASE));
      if (needed.includes('tinyFace')     && !modelsLoaded.tinyFace)     loaders.push(fa.nets.tinyFaceDetector.loadFromUri(MODELS_BASE));
      if (needed.includes('landmarks68')  && !modelsLoaded.landmarks68)  loaders.push(fa.nets.faceLandmark68Net.loadFromUri(MODELS_BASE));
      if (needed.includes('expressions')  && !modelsLoaded.expressions)  loaders.push(fa.nets.faceExpressionNet.loadFromUri(MODELS_BASE));
      if (needed.includes('ageGender')    && !modelsLoaded.ageGender)    loaders.push(fa.nets.ageGenderNet.loadFromUri(MODELS_BASE));
      if (needed.includes('faceRec')      && !modelsLoaded.faceRec)      loaders.push(fa.nets.faceRecognitionNet.loadFromUri(MODELS_BASE));
      setLoadProgress(30);
      await Promise.all(loaders);
      setLoadProgress(100);
      setModelsLoaded(prev => ({ ...prev, ...Object.fromEntries(needed.map(m => [m, true])) }));
    } catch (e) { console.error('face-api load error:', e); }
    setLoadingModel(null);
  };

  const getImg = (id) => {
    const el = document.getElementById(id);
    if (!el?.complete || !el.naturalWidth) return null;
    return el;
  };

  // ── DETECT ───────────────────────────────────────────────────
  const runDetect = async () => {
    const img = getImg('detect-img'); if (!img) return;
    const fa  = window.faceapi;
    const t0  = performance.now();
    const detections = await fa.detectAllFaces(img, new fa.SsdMobilenetv1Options({ minConfidenceScore: 0.4 }));
    setDetectTime(Math.round(performance.now() - t0));
    const mapped = detections.map(d => ({ detection: d }));
    setDetectResult(mapped);
    drawDetections(detectCanvas.current, img, mapped);
  };

  // ── LANDMARKS ─────────────────────────────────────────────────
  const runLandmarks = async () => {
    const img = getImg('lm-img'); if (!img) return;
    const fa  = window.faceapi;
    const t0  = performance.now();
    const detections = await fa.detectAllFaces(img, new fa.TinyFaceDetectorOptions())
      .withFaceLandmarks();
    setLmTime(Math.round(performance.now() - t0));
    setLmResult(detections);
    drawLandmarks(lmCanvas.current, img, detections.map(d => d.landmarks));
  };

  // ── EXPRESSIONS ───────────────────────────────────────────────
  const runExpressions = async () => {
    const img = getImg('expr-img'); if (!img) return;
    const fa  = window.faceapi;
    const t0  = performance.now();
    const detections = await fa.detectAllFaces(img, new fa.TinyFaceDetectorOptions())
      .withFaceLandmarks().withFaceExpressions();
    setExprTime(Math.round(performance.now() - t0));
    setExprResult(detections);
    // Draw on canvas
    const canvas = exprCanvas.current; if (!canvas) return;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    detections.forEach(({ detection, expressions }) => {
      const { box } = detection.box ? detection : detection.detection;
      const b = box || detection.detection?.box || {};
      const top = Object.entries(expressions).sort((a, b) => b[1] - a[1])[0];
      if (!top) return;
      ctx.strokeStyle = EMOTION_COLORS[top[0]] || ACCENT; ctx.lineWidth = 3;
      ctx.strokeRect(b.x, b.y, b.width, b.height);
      ctx.fillStyle = EMOTION_COLORS[top[0]] || ACCENT;
      ctx.fillRect(b.x, b.y - 24, top[0].length * 9 + 40, 24);
      ctx.fillStyle = '#000'; ctx.font = 'bold 13px monospace';
      ctx.fillText(`${EMOTION_EMOJIS[top[0]]} ${top[0]}`, b.x + 4, b.y - 6);
    });
  };

  // ── AGE/GENDER ────────────────────────────────────────────────
  const runAgeGender = async () => {
    const img = getImg('ag-img'); if (!img) return;
    const fa  = window.faceapi;
    const t0  = performance.now();
    const detections = await fa.detectAllFaces(img, new fa.TinyFaceDetectorOptions())
      .withFaceLandmarks().withAgeAndGender();
    setAgTime(Math.round(performance.now() - t0));
    setAgResult(detections);
    const canvas = agCanvas.current; if (!canvas) return;
    canvas.width = img.naturalWidth; canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    detections.forEach(({ detection, age, gender, genderProbability }) => {
      const b = detection.box;
      const col = gender === 'female' ? ACCENT : '#60a5fa';
      ctx.strokeStyle = col; ctx.lineWidth = 3;
      ctx.strokeRect(b.x, b.y, b.width, b.height);
      const label = `${Math.round(age)}yr ${gender} ${(genderProbability*100).toFixed(0)}%`;
      ctx.fillStyle = col + 'cc'; ctx.fillRect(b.x, b.y - 24, label.length * 8, 24);
      ctx.fillStyle = '#000'; ctx.font = 'bold 12px monospace';
      ctx.fillText(label, b.x + 4, b.y - 6);
    });
  };

  // ── RECOGNITION ───────────────────────────────────────────────
  const runRecognition = async () => {
    const img1 = getImg('rec-img1'), img2 = getImg('rec-img2');
    if (!img1 || !img2) return;
    const fa = window.faceapi;
    try {
      const [desc1, desc2] = await Promise.all([
        fa.detectSingleFace(img1, new fa.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor(),
        fa.detectSingleFace(img2, new fa.TinyFaceDetectorOptions()).withFaceLandmarks().withFaceDescriptor(),
      ]);
      if (!desc1 || !desc2) { setRecResult({ error: 'Could not detect face in one or both images' }); return; }
      const distance = fa.euclideanDistance(desc1.descriptor, desc2.descriptor);
      setRecResult({ distance, match: distance < 0.6, desc1: Array.from(desc1.descriptor).slice(0, 8) });
    } catch (e) { setRecResult({ error: e.message }); }
  };

  // ── RENDER ───────────────────────────────────────────────────
  const ModelsNeeded = ({ models, children }) => {
    const allLoaded = models.every(m => modelsLoaded[m]);
    if (allLoaded) return children;
    return (
      <div style={{ padding: '20px', background: 'var(--raised)', border: `1px solid ${ACCENT}33`, borderRadius: '4px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: ACCENT, fontWeight: 700, marginBottom: '4px' }}>Models required: {models.join(', ')}</div>
          <div style={{ fontSize: '10px', color: 'var(--t3)' }}>Models load from GitHub (~5–15MB each). Cached after first load.</div>
        </div>
        {loadingModel ? (
          <div style={{ fontSize: '11px', color: 'var(--gold)', fontFamily: 'JetBrains Mono' }}>⟳ Loading {loadingModel}…</div>
        ) : (
          <Btn onClick={() => loadModels(models)} color={ACCENT}>LOAD MODELS →</Btn>
        )}
      </div>
    );
  };

  const UploadAndRun = ({ id, src, onSrc, onRun, color = ACCENT, label = 'ANALYZE →' }) => (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
      <label style={{ padding: '8px 16px', background: color + '18', border: `1px solid ${color}`, color, fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
        📁 UPLOAD IMAGE
        <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) onSrc(URL.createObjectURL(f)); }} style={{ display: 'none' }} />
      </label>
      {src && <Btn onClick={onRun} color={color}>{label}</Btn>}
      {src && <img id={id} src={src} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />}
    </div>
  );

  return (
    <div className="anim-fadeup" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', gap: '3px', background: 'var(--deep)', padding: '4px', borderRadius: '3px', border: '1px solid var(--b0)', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: '6px 14px', border: `1px solid ${tab === t ? ACCENT : 'transparent'}`, background: tab === t ? ACCENT + '18' : 'transparent', color: tab === t ? ACCENT : 'var(--t3)', fontSize: '10px', cursor: 'pointer', borderRadius: '2px', fontFamily: 'JetBrains Mono', whiteSpace: 'nowrap', fontWeight: tab === t ? 700 : 400 }}>{t.toUpperCase().replace('_',' ')}</button>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Panel title="FACE-API.JS — OVERVIEW" accent={ACCENT}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.85 }}>
                <strong style={{ color: ACCENT }}>face-api.js</strong> wraps TensorFlow.js with 5 specialized face models: SSD/TinyFaceDetector (localization), FaceLandmark68Net (68-point geometry), FaceExpressionNet (7 emotions), AgeGenderNet, and FaceRecognitionNet (128-d descriptor for identity matching).<br /><br />
                All models are MIT-licensed and hosted on GitHub. Each loads independently — use only what you need. Models are small (3–10MB) and cache in IndexedDB.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                {[
                  ['SSD MobileNetV1','High-accuracy detector','~5MB',ACCENT],
                  ['TinyFaceDetector','Fast detector','~190KB','var(--cyan)'],
                  ['FaceLandmark68Net','68 facial landmarks','~350KB','var(--green)'],
                  ['FaceExpressionNet','7 emotion classes','~310KB','var(--gold)'],
                  ['AgeGenderNet','Age + gender estimate','~420KB','var(--violet)'],
                  ['FaceRecognitionNet','128d identity embed','~6.2MB','var(--orange)'],
                ].map(([name, desc, size, color]) => (
                  <div key={name} style={{ display: 'flex', gap: '8px', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color }}>{name}</div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)' }}>{desc}</div>
                    </div>
                    <Tag color={color}>{size}</Tag>
                  </div>
                ))}
              </div>
            </div>
          </Panel>

          <Panel title="QUICK API" accent={ACCENT}>
            <pre style={{ margin: 0, padding: '14px', background: 'var(--deep)', borderRadius: '3px', fontSize: '10px', color: 'var(--t2)', fontFamily: 'JetBrains Mono', lineHeight: 1.7, overflowX: 'auto' }}>
{`// Load models from CDN
await faceapi.nets.tinyFaceDetector.loadFromUri('./models');
await faceapi.nets.faceLandmark68Net.loadFromUri('./models');
await faceapi.nets.faceExpressionNet.loadFromUri('./models');

// Run full pipeline on <img> or <video>
const detections = await faceapi
  .detectAllFaces(imgEl, new faceapi.TinyFaceDetectorOptions())
  .withFaceLandmarks()
  .withFaceExpressions()
  .withAgeAndGender()
  .withFaceDescriptors();

// detections[0].expressions → { happy: 0.9, sad: 0.02, ... }
// detections[0].age → 24.3
// detections[0].gender → 'female'
// detections[0].descriptor → Float32Array(128)`}
            </pre>
          </Panel>
        </div>
      )}

      {/* ── DETECT ── */}
      {tab === 'detect' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ModelsNeeded models={['ssd']}>
            <Panel title="FACE DETECTION — SSD MOBILENETV1" accent={ACCENT}>
              <UploadAndRun id="detect-img" src={detectImg} onSrc={setDetectImg} onRun={runDetect} label="DETECT FACES →" />
              {detectImg && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <canvas ref={detectCanvas} style={{ maxWidth: '320px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  {detectResult && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '10px', letterSpacing: '.1em' }}>
                        {detectResult.length} FACE(S) DETECTED
                        {detectTime && <> · </>}
                        {detectTime && <InferenceTag ms={detectTime} />}
                      </div>
                      {detectResult.map(({ detection }, i) => (
                        <div key={i} style={{ padding: '10px 12px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '6px', border: `1px solid ${P[i % P.length]}33` }}>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: P[i % P.length], marginBottom: '6px' }}>Face {i+1}</div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <Tag color={P[i%P.length]}>score: {(detection.score * 100).toFixed(1)}%</Tag>
                            <Tag color="var(--t3)">x: {Math.round(detection.box.x)}</Tag>
                            <Tag color="var(--t3)">y: {Math.round(detection.box.y)}</Tag>
                            <Tag color="var(--t3)">{Math.round(detection.box.width)}×{Math.round(detection.box.height)}px</Tag>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          </ModelsNeeded>
        </div>
      )}

      {/* ── LANDMARKS ── */}
      {tab === 'landmarks' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ModelsNeeded models={['tinyFace','landmarks68']}>
            <Panel title="68 FACIAL LANDMARKS" accent="var(--green)">
              <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--green)' }}>FaceLandmark68Net</strong> predicts 68 keypoints: 17 jaw, 5 left eyebrow, 5 right eyebrow, 9 nose, 6 left eye, 6 right eye, 20 mouth. Based on Dlib's 68-point model.
              </div>
              <UploadAndRun id="lm-img" src={lmImg} onSrc={setLmImg} onRun={runLandmarks} color="var(--green)" label="DETECT LANDMARKS →" />
              {lmImg && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <canvas ref={lmCanvas} style={{ maxWidth: '320px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  {lmResult?.length > 0 && (
                    <div>
                      <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>{lmTime && <InferenceTag ms={lmTime} />}</div>
                      {[['jaw', 0, 16, '#34d399'],['left eyebrow', 17, 21, '#f59e0b'],['right eyebrow', 22, 26, '#f59e0b'],['nose', 27, 35, '#60a5fa'],['left eye', 36, 41, '#f472b6'],['right eye', 42, 47, '#f472b6'],['mouth', 48, 67, '#fb923c']].map(([group, start, end, color]) => (
                        <div key={group} style={{ display: 'flex', gap: '8px', padding: '6px 10px', background: 'var(--raised)', borderRadius: '3px', marginBottom: '4px' }}>
                          <div style={{ width: '8px', height: '8px', background: color, borderRadius: '50%', alignSelf: 'center', flexShrink: 0 }} />
                          <span style={{ fontSize: '11px', color, flex: 1, fontFamily: 'JetBrains Mono', fontWeight: 700 }}>{group}</span>
                          <Tag color={color}>pts {start}–{end}</Tag>
                          <Tag color="var(--t3)">{end - start + 1} points</Tag>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </Panel>
          </ModelsNeeded>
        </div>
      )}

      {/* ── EXPRESSIONS ── */}
      {tab === 'expressions' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ModelsNeeded models={['tinyFace','landmarks68','expressions']}>
            <Panel title="EXPRESSION RECOGNITION — 7 EMOTIONS" accent="var(--gold)">
              <UploadAndRun id="expr-img" src={exprImg} onSrc={setExprImg} onRun={runExpressions} color="var(--gold)" label="DETECT EMOTION →" />
              {exprImg && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <canvas ref={exprCanvas} style={{ maxWidth: '300px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  {exprResult?.map((d, i) => {
                    const exprs = Object.entries(d.expressions || {}).sort((a, b) => b[1] - a[1]);
                    const top = exprs[0];
                    return (
                      <div key={i}>
                        {top && (
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '14px', background: (EMOTION_COLORS[top[0]] || ACCENT) + '18', border: `1px solid ${EMOTION_COLORS[top[0]] || ACCENT}44`, borderRadius: '4px', marginBottom: '12px' }}>
                            <div style={{ fontSize: '40px' }}>{EMOTION_EMOJIS[top[0]]}</div>
                            <div>
                              <div style={{ fontSize: '18px', fontWeight: 700, fontFamily: 'Syne', color: EMOTION_COLORS[top[0]] || ACCENT }}>{top[0].toUpperCase()}</div>
                              <div style={{ fontSize: '11px', color: 'var(--t3)' }}>{(top[1] * 100).toFixed(1)}% confidence</div>
                            </div>
                            {exprTime && <div style={{ marginLeft: 'auto' }}><InferenceTag ms={exprTime} /></div>}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {exprs.map(([emotion, score]) => (
                            <div key={emotion} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontSize: '18px' }}>{EMOTION_EMOJIS[emotion]}</span>
                              <span style={{ fontSize: '11px', color: EMOTION_COLORS[emotion] || 'var(--t2)', fontFamily: 'JetBrains Mono', minWidth: '90px', fontWeight: emotion === top[0] ? 700 : 400 }}>{emotion}</span>
                              <div style={{ flex: 1, height: '6px', background: 'var(--deep)', borderRadius: '3px' }}>
                                <div style={{ height: '100%', width: `${score * 100}%`, background: EMOTION_COLORS[emotion] || ACCENT, borderRadius: '3px' }} />
                              </div>
                              <span style={{ fontSize: '11px', color: EMOTION_COLORS[emotion] || 'var(--t3)', fontFamily: 'JetBrains Mono', minWidth: '42px', textAlign: 'right', fontWeight: emotion === top[0] ? 700 : 400 }}>{(score * 100).toFixed(1)}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Panel>
          </ModelsNeeded>
        </div>
      )}

      {/* ── AGE/GENDER ── */}
      {tab === 'age_gender' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ModelsNeeded models={['tinyFace','landmarks68','ageGender']}>
            <Panel title="AGE & GENDER ESTIMATION" accent="var(--violet)">
              <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--violet)' }}>AgeGenderNet</strong> jointly predicts age (regression, ±5 years MAE) and gender (binary classification with confidence). Trained on diverse face images. Works on multiple faces simultaneously.
              </div>
              <UploadAndRun id="ag-img" src={agImg} onSrc={setAgImg} onRun={runAgeGender} color="var(--violet)" label="ESTIMATE AGE/GENDER →" />
              {agImg && (
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '14px', alignItems: 'start' }}>
                  <canvas ref={agCanvas} style={{ maxWidth: '300px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                  {agResult?.map((d, i) => (
                    <div key={i} style={{ padding: '14px', background: 'var(--raised)', border: '1px solid var(--violet)33', borderRadius: '4px', marginBottom: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--violet)', marginBottom: '10px' }}>Face {i+1}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div style={{ padding: '10px', background: 'var(--deep)', borderRadius: '3px', textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px' }}>ESTIMATED AGE</div>
                          <div style={{ fontSize: '28px', fontWeight: 700, fontFamily: 'Syne', color: 'var(--violet)' }}>{Math.round(d.age)}</div>
                          <div style={{ fontSize: '10px', color: 'var(--t3)' }}>years ± 5 MAE</div>
                        </div>
                        <div style={{ padding: '10px', background: 'var(--deep)', borderRadius: '3px', textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '4px' }}>GENDER</div>
                          <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: 'Syne', color: d.gender === 'female' ? ACCENT : '#60a5fa' }}>{d.gender}</div>
                          <div style={{ fontSize: '10px', color: 'var(--t3)' }}>{(d.genderProbability * 100).toFixed(1)}% confidence</div>
                        </div>
                      </div>
                      {agTime && <div style={{ marginTop: '8px' }}><InferenceTag ms={agTime} /></div>}
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          </ModelsNeeded>
        </div>
      )}

      {/* ── RECOGNITION ── */}
      {tab === 'recognition' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ModelsNeeded models={['tinyFace','landmarks68','faceRec']}>
            <Panel title="FACE RECOGNITION — FACENET 128D MATCHING" accent="var(--orange)">
              <div style={{ fontSize: '11px', color: 'var(--t3)', marginBottom: '14px', lineHeight: 1.7 }}>
                <strong style={{ color: 'var(--orange)' }}>FaceRecognitionNet</strong> generates a 128-dimensional descriptor per face. Euclidean distance &lt; 0.6 = same person. Inspired by FaceNet (Schroff et al. 2015). Upload two face photos to compare.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                {[1, 2].map(n => (
                  <div key={n}>
                    <div style={{ fontSize: '9px', color: 'var(--t3)', marginBottom: '8px', letterSpacing: '.1em' }}>FACE {n}</div>
                    <label style={{ display: 'block', padding: '8px 16px', background: 'var(--orange)18', border: '1px solid var(--orange)', color: 'var(--orange)', fontSize: '10px', cursor: 'pointer', borderRadius: '3px', fontFamily: 'JetBrains Mono', fontWeight: 700, textAlign: 'center', marginBottom: '8px' }}>
                      📁 UPLOAD FACE {n}
                      <input type="file" accept="image/*" onChange={e => { const f = e.target.files[0]; if (f) (n === 1 ? setRecImg1 : setRecImg2)(URL.createObjectURL(f)); }} style={{ display: 'none' }} />
                    </label>
                    {(n === 1 ? recImg1 : recImg2) && (
                      <>
                        <img id={`rec-img${n}`} src={n === 1 ? recImg1 : recImg2} alt="" style={{ display: 'none' }} crossOrigin="anonymous" />
                        <img src={n === 1 ? recImg1 : recImg2} alt="" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '4px', border: '1px solid var(--b0)', display: 'block' }} />
                      </>
                    )}
                  </div>
                ))}
              </div>
              {recImg1 && recImg2 && (
                <div style={{ marginBottom: '14px' }}>
                  <Btn onClick={runRecognition} color="var(--orange)">COMPARE FACES →</Btn>
                </div>
              )}
              {recResult && (
                recResult.error ? (
                  <div style={{ padding: '12px', background: 'var(--red)18', border: '1px solid var(--red)33', borderRadius: '4px', fontSize: '11px', color: 'var(--red)' }}>{recResult.error}</div>
                ) : (
                  <div style={{ padding: '20px', background: (recResult.match ? 'var(--green)' : 'var(--red)') + '12', border: `1px solid ${recResult.match ? 'var(--green)' : 'var(--red)'}44`, borderRadius: '6px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ fontSize: '36px' }}>{recResult.match ? '✅' : '❌'}</div>
                      <div>
                        <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: 'Syne', color: recResult.match ? 'var(--green)' : 'var(--red)' }}>
                          {recResult.match ? 'SAME PERSON' : 'DIFFERENT PERSON'}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--t3)' }}>Euclidean distance threshold: 0.6</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <Tag color={recResult.match ? 'var(--green)' : 'var(--red)'}>distance: {recResult.distance?.toFixed(4)}</Tag>
                      <Tag color="var(--t3)">128d descriptor</Tag>
                      <Tag color={recResult.match ? 'var(--green)' : 'var(--red)'}>{recResult.match ? '< 0.6 → match' : '≥ 0.6 → no match'}</Tag>
                    </div>
                    {recResult.desc1 && (
                      <div style={{ marginTop: '10px', fontSize: '9px', color: 'var(--t3)', fontFamily: 'JetBrains Mono' }}>
                        descriptor preview: [{recResult.desc1.map(v => v.toFixed(3)).join(', ')}…]
                      </div>
                    )}
                  </div>
                )
              )}
            </Panel>
          </ModelsNeeded>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab === 'concepts' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          {[
            { title:'SSD Face Detection', color:ACCENT, desc:'SSD (Single Shot Detector) uses a feature pyramid + anchor boxes at multiple scales to detect faces in one forward pass. Unlike two-stage detectors (R-CNN), no region proposal step. SSD MobileNetV1 balances accuracy (>99% mAP on FDDB) with speed (~30ms/frame browser).' },
            { title:'68 Dlib Landmarks', color:'var(--green)', desc:'The 68-point model comes from Dlib\'s face alignment. Points 0-16: jaw. 17-21: left eyebrow. 22-26: right eyebrow. 27-30: nose bridge. 31-35: nose tip. 36-41: left eye. 42-47: right eye. 48-59: outer lips. 60-67: inner lips. Used for face alignment, expression analysis, AR filters.' },
            { title:'FaceNet 128d Embeddings', color:'var(--orange)', desc:'Schroff et al. 2015: train a CNN with triplet loss — minimize distance between same-person pairs, maximize between different-person pairs. Converges to a 128d embedding space where Euclidean distance reflects identity similarity. Threshold 0.6 gives ~99.6% accuracy on LFW benchmark.' },
            { title:'Expression Classification', color:'var(--gold)', desc:'FaceExpressionNet: lightweight CNN (310KB) trained on labeled expression datasets. 7 output classes: happy, sad, angry, fearful, disgusted, surprised, neutral. Probabilities sum to 1 (softmax). Runs after face detection + alignment (using 68 landmarks to normalize face rotation).' },
            { title:'Age/Gender Network', color:'var(--violet)', desc:'Trained on Adience dataset (2,284 subjects, 7 age groups). AgeGenderNet jointly predicts age (regression → continuous value) and gender (binary). MAE typically ±4-6 years. Gender accuracy ~90%. Sensitive to lighting, expression, and image resolution.' },
            { title:'Ethical Considerations', color:'var(--red)', desc:'Face recognition raises significant privacy concerns. Trained on datasets that may not represent all demographics equally. Gender classification is binary — doesn\'t reflect gender diversity. Age estimation has demographic biases. Use only with explicit user consent. Never build surveillance systems or identification without legal compliance.' },
          ].map(({ title, color, desc }) => (
            <div key={title} style={{ padding: '16px', background: 'var(--raised)', border: `1px solid ${color}33`, borderRadius: '4px' }}>
              <div style={{ color, fontWeight: 700, fontSize: '13px', marginBottom: '8px' }}>{title}</div>
              <div style={{ fontSize: '11px', color: 'var(--t3)', lineHeight: 1.75 }}>{desc}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
